import { accountApi } from "@shared/apis"
import { Token } from "@shared/apis/account"
import { EXCEPTION, GROUP } from "@shared/config"
import { Status } from "@shared/constants"
import { Account, Viewer } from "@shared/contexts"
import {
  exceptionToKick,
  getChattingToken,
  getIsRefreshToken,
  getSchdueId,
  getUsername,
  getUuid,
  kick,
  removeTokens,
  setUsername,
  setUuid,
} from "@shared/modules"
import { viewerService } from "@shared/services"

const check = async (
  scheduleId: string | undefined,
  ticket: string,
  username: string,
  partnerId?: number | string | undefined,
  isVodPlayer?: boolean,
  otp?: string,
) => {
  if (getIsRefreshToken()) {
    try {
      await accountApi.token.refresh()
      return true
    } catch {
      return kick("인증정보가 올바르지 않습니다")
    }
  }

  try {
    if (otp) {
      await accountApi.checkOtp(scheduleId!, otp)
      return true
    }

    await accountApi.check(
      scheduleId!,
      ticket,
      username,
      partnerId,
      isVodPlayer,
    )
    return true
  } catch (err) {
    const defaultErrorMessage =
      "방송 참여 중 오류가 발생하였습니다. 다시 참여 부탁 드립니다. 지속적으로 문제가 발생시 고객센터로 문의를 해주세요."
    if (!err) {
      return kick(defaultErrorMessage)
    }

    const status = (err as any).response?.status

    switch (status) {
      case Status.ANONYMOUS:
        // VOD
        if (window.location.pathname.includes("/vods")) {
          return kick("VOD 시청을 위해서 로그인이 필요합니다.")
        }
        // 라이브 비로그인 진입시 406 발생 → FE에서 bypass
        return true
      case Status.INVALID_USER:
        return kick("인증정보가 올바르지 않습니다")
      default:
        return kick(defaultErrorMessage)
    }
  }
}

const signing = async (
  scheduleId: string | undefined,
  setAccount: React.Dispatch<React.SetStateAction<Account>>,
  ticket: string,
  username: string,
  isOnlyPlayer: string | undefined,
  partnerId?: number | string | undefined,
  isVodPlayer?: boolean,
  otp?: string,
) => {
  if (!otp && !isOnlyPlayer && getUsername() && username !== getUsername()) {
    removeTokens()
  }

  // 숏폼 비회원(ticket은 있고 username이 없는 진입)은 check API를 호출하지 않는다.
  // 대신 숏폼 진입 검증(validateShortsEntry) 응답으로 토큰을 받으므로 check를 건너뛴다.
  // OTP 진입(어드민 등)은 username이 없어도 checkOtp로 토큰을 받아야 하므로 제외한다.
  const isAnonymousShortsEntry =
    !otp && !username && window.location.pathname.includes("/shorts/")

  if (!isAnonymousShortsEntry && scheduleId !== getSchdueId()) {
    const isSucceed = await check(
      scheduleId!,
      ticket,
      username,
      partnerId,
      isVodPlayer,
      otp,
    )

    if (!isSucceed) return
  }

  const viewer = await viewerService.get(isAnonymousShortsEntry)

  if (viewer && viewer.isKicked) {
    kick("접근이 금지된 회원입니다.")
    return
  }

  setAccount((prevAccount) => ({
    ...prevAccount,
    viewer: viewer!,
  }))

  if (isOnlyPlayer) return

  try {
    const fbUser = await (viewer!.role === "ANONYMOUS"
      ? accountApi.loginAnonymous()
      : accountApi.login(getChattingToken()!))

    if (viewer && !GROUP.STREAMER.includes(viewer.role)) {
      setUsername(viewer.username)
    }

    setAccount((prevAccount) => ({
      ...prevAccount,
      viewer: {
        ...prevAccount.viewer,
        userId:
          viewer!.role === "ANONYMOUS"
            ? fbUser.user?.uid
            : prevAccount.viewer?.userId,
        uuid: getUuid() || setUuid(),
      } as Viewer,
      currentUser: fbUser.user as firebase.User,
    }))
  } catch (e) {
    exceptionToKick(EXCEPTION.FBConnection)
  }
}

const refreshToken = (): Promise<Token | undefined> => {
  return accountApi.token.refresh()
}

export default { signing, check, refreshToken }
